import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useProjectenKort } from '@/features/projectlijst/hooks';
import { useRechten } from '@/features/relaties/ui/Onderdelen';
import { BusinesscaseComparison } from '@/components/businesscase/BusinesscaseComparison';
import { BusinesscaseLijst } from '@/features/businesscases/BusinesscaseLijst';
import {
  filterNaarUrl, filterUitUrl, haalBusinesscases, type BcClient, type LijstBusinesscase, type LijstFilter, type Weergave,
} from '@/features/businesscases/data/lijst';
import { archiveerBusinesscase, dupliceerBusinesscase, herstelBusinesscase, maakBusinesscase } from '@/features/businesscases/data/acties';
import { BUSINESSCASES_SLEUTEL, naBusinesscaseWijziging } from '@/features/businesscases/data/sleutels';

/* /crm/businesscases. De lijst zelf staat in src/features/businesscases/BusinesscaseLijst.tsx;
   hier de gegevens, de filter in de URL, en de dialogen voor nieuw en dupliceren (ADR 0023).
   ?project=<id> opent "Nieuwe businesscase" met dat project al gekozen. */

const client = supabase as unknown as BcClient;

async function gebruiker(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

/** Voor de vergelijkweergave: de uitgerekende cijfers in de vorm die BusinesscaseComparison kent. */
const alsVergelijk = (b: LijstBusinesscase) => ({
  id: b.id,
  name: b.naam,
  roi_year1: b.cijfers.roiJaar1 as number,
  roi_calculation_mode: b.modus,
  net_investment: b.cijfers.nettoInvestering,
  payback_period: b.cijfers.terugverdientijd,
  projects: b.project ? { name: b.project.naam, project_number: b.project.nummer ?? undefined } : undefined,
});

export default function CRMBusinesscases() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const { magBeheren } = useRechten();
  const [searchParams, setSearchParams] = useSearchParams();
  const vooraf = searchParams.get('project');
  const [nieuw, setNieuw] = useState<{ open: boolean; naam: string; projectId: string; notities: string }>(() => ({
    open: Boolean(vooraf), naam: '', projectId: vooraf ?? '', notities: '',
  }));
  const [kopie, setKopie] = useState<{ bron: LijstBusinesscase; naam: string } | null>(null);
  const [bezig, setBezig] = useState(false);

  const lijst = useQuery({ queryKey: [...BUSINESSCASES_SLEUTEL, 'lijst'], queryFn: () => haalBusinesscases(client) });
  const projecten = useProjectenKort({ enabled: nieuw.open });
  const filter = useMemo(() => filterUitUrl(searchParams), [searchParams]);
  const weergave: Weergave = searchParams.get('weergave') === 'vergelijken' ? 'vergelijken' : 'lijst';
  const zetUrl = useCallback((f: LijstFilter, w: Weergave) => {
    setSearchParams({ ...filterNaarUrl(f), ...(w === 'vergelijken' ? { weergave: w } : {}) }, { replace: true });
  }, [setSearchParams]);

  const fout = (titel: string, e: unknown) => toast({ title: titel, description: (e as Error)?.message ?? String(e), variant: 'destructive' });

  const maak = async () => {
    setBezig(true);
    try {
      const id = await maakBusinesscase(client, { projectId: nieuw.projectId, naam: nieuw.naam, notities: nieuw.notities, gebruiker: await gebruiker() });
      naBusinesscaseWijziging(qc);
      setNieuw({ open: false, naam: '', projectId: '', notities: '' });
      navigate(`/crm/businesscases/${id}`);
    } catch (e) {
      fout('Aanmaken mislukt', e);
    } finally {
      setBezig(false);
    }
  };

  const dupliceer = async () => {
    if (!kopie) return;
    setBezig(true);
    try {
      const id = await dupliceerBusinesscase(client, kopie.bron.id, kopie.naam, await gebruiker());
      naBusinesscaseWijziging(qc);
      setKopie(null);
      navigate(`/crm/businesscases/${id}`);
    } catch (e) {
      fout('Dupliceren mislukt', e);
    } finally {
      setBezig(false);
    }
  };

  const wissel = async (b: LijstBusinesscase, archiveren: boolean) => {
    try {
      await (archiveren ? archiveerBusinesscase : herstelBusinesscase)(client, b.id);
      toast({ title: archiveren ? 'Businesscase gearchiveerd' : 'Businesscase teruggezet', description: `"${b.naam}"` });
    } catch (e) {
      fout(archiveren ? 'Archiveren mislukt' : 'Terugzetten mislukt', e);
    }
    naBusinesscaseWijziging(qc);
  };

  const sluitNieuw = () => {
    setNieuw({ open: false, naam: '', projectId: '', notities: '' });
    if (vooraf) {
      const p = new URLSearchParams(searchParams);
      p.delete('project');
      setSearchParams(p, { replace: true });
    }
  };

  return (
    <>
      <BusinesscaseLijst
        cases={lijst.data ?? []}
        laden={lijst.isPending}
        fout={lijst.error as Error | null}
        filter={filter}
        onFilter={(f) => zetUrl(f, weergave)}
        weergave={weergave}
        onWeergave={(w) => zetUrl(filter, w)}
        magBeheren={magBeheren}
        onNieuw={() => setNieuw({ open: true, naam: '', projectId: '', notities: '' })}
        onDupliceer={(b) => setKopie({ bron: b, naam: `${b.naam} (kopie)` })}
        onArchiveer={(b) => wissel(b, true)}
        onHerstel={(b) => wissel(b, false)}
        vergelijk={(cases) => <BusinesscaseComparison businesscases={cases.filter((b) => !b.leeg).map(alsVergelijk)} />}
      />

      <Dialog open={nieuw.open} onOpenChange={(o) => !o && sluitNieuw()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nieuwe businesscase</DialogTitle>
            <DialogDescription>Een businesscase hoort bij een project. Hij begint met tien lege jaren.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="bc-naam">Naam</Label>
              <Input id="bc-naam" placeholder="bijv. Scenario A, 50 kW met PV" value={nieuw.naam} onChange={(e) => setNieuw({ ...nieuw, naam: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bc-project">Project</Label>
              <Select value={nieuw.projectId} onValueChange={(projectId) => setNieuw({ ...nieuw, projectId })}>
                <SelectTrigger id="bc-project"><SelectValue placeholder="Kies een project" /></SelectTrigger>
                <SelectContent>
                  {(projecten.data ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{[p.project_number, p.name].filter(Boolean).join(' · ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bc-notities">Notities (optioneel)</Label>
              <Textarea id="bc-notities" value={nieuw.notities} onChange={(e) => setNieuw({ ...nieuw, notities: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={sluitNieuw}>Annuleren</Button>
            <Button onClick={maak} disabled={bezig || !nieuw.naam.trim() || !nieuw.projectId}>Aanmaken</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(kopie)} onOpenChange={(o) => !o && setKopie(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Businesscase dupliceren</DialogTitle>
            <DialogDescription>Een kopie met alle kosten, subsidies, extra kosten en opbrengsten, bij hetzelfde project.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="bc-kopie">Naam van de kopie</Label>
            <Input id="bc-kopie" value={kopie?.naam ?? ''} onChange={(e) => kopie && setKopie({ ...kopie, naam: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKopie(null)}>Annuleren</Button>
            <Button onClick={dupliceer} disabled={bezig || !kopie?.naam.trim()}>Dupliceren</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
